import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle, UserCog, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RoleBadgeProps {
  role: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// Role color configurations with nice styling
const ROLE_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
  operator: {
    color: 'bg-blue-500 hover:bg-blue-600 border-blue-600',
    icon: <Shield className="w-3 h-3" />,
  },
  quality: {
    color: 'bg-purple-500 hover:bg-purple-600 border-purple-600',
    icon: <CheckCircle className="w-3 h-3" />,
  },
  site_manager: {
    color: 'bg-orange-500 hover:bg-orange-600 border-orange-600',
    icon: <UserCog className="w-3 h-3" />,
  },
  admin: {
    color: 'bg-red-500 hover:bg-red-600 border-red-600',
    icon: <Settings className="w-3 h-3" />,
  },
};

export function RoleBadge({ role, size = 'md', className }: RoleBadgeProps) {
  const { t } = useTranslation();

  const config = ROLE_CONFIG[role] || {
    color: 'bg-gray-500 hover:bg-gray-600 border-gray-600',
    icon: <Shield className="w-3 h-3" />,
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-1 gap-1.5',
    lg: 'text-base px-3 py-1.5 gap-2',
  };

  return (
    <Badge
      className={cn(
        config.color,
        'text-white font-medium flex items-center border',
        sizeClasses[size],
        className
      )}
    >
      {config.icon}
      <span>{t(`roles.${role}`)}</span>
    </Badge>
  );
}

interface RoleBadgeListProps {
  roles: string[] | null | undefined;
  maxDisplay?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function RoleBadgeList({ roles, maxDisplay = 3, size = 'sm', className }: RoleBadgeListProps) {
  if (!roles || roles.length === 0) return null;

  const displayRoles = roles.slice(0, maxDisplay);
  const remaining = roles.length - maxDisplay;

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {displayRoles.map((role) => (
        <RoleBadge key={role} role={role} size={size} />
      ))}
      {remaining > 0 && (
        <Badge variant="outline" className="text-xs border-muted">
          +{remaining}
        </Badge>
      )}
    </div>
  );
}
